import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as http from "../../../utils/http";
import { trackContinueToShipping } from "../../../utils/otcPopupExperiment";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ProductSelectionPanel from './components/ProductSelectionPanel';
import Legend from './components/Legend';
// import "./CannaleoFreeDosageLayout.css"
import HeaderController from '../../../components/Headers/HeaderController';
import Footer from '../../../components/Footer/Footer';
import LoadingModal from '../../../components/Modals/LoadingModal/LoadingModal';
import ServerError from '../../../components/ServerError/ServerError';
import StickyNotification from '../../../components/StickyNotification/StickyNotification';
import { mergeArrays } from '../helpers';
import ProductSearch from './components/ProductSearch';
import ProductFiltersControl from './components/ProductFiltersControl';
import { FilterMenu } from './components/FiltersMenu';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import StickySubmitButton from './components/StickySubmitButton';
import {CANNALEO, DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT, FLOWZZ, ORDER_FLOWS, CANNABIS_LIMIT_ERROR_CODES } from '../../../utils/consts';
import { AdditionalData, PredefinedFiltersType, ChooseTreatmentProductType, ChooseTreatmentProductItemType, SortByType, SORT_BY_THC_ASC, SORT_BY_THC_DESC, CannabisLimitsResult } from '../types';
import { parseRangeFilter, sortRangeFilter, sortProducts, getMinPricePerQuantity, isOrderRateLimited, getCannabisLimitMessage } from '../helpersV2';
import { loadCustomerFromSession } from '../../../customHooks/useCustomer';
import { useCartContext } from '../../../customHooks/useCart';
import PortaledModal from '../../../components/Modals/PortaledModal';
import Disclaimer from '../components/Disclaimers/Disclaimer';
import { translate } from '../../../utils/translator';

type ProductItem = ChooseTreatmentProductItemType & {
  catalog_id: number,
  preSelected: boolean,
}

export type Product = Omit<ChooseTreatmentProductType, "items"> & {
  items: ProductItem[],
  preSelected: boolean,
  isSelected: boolean,
}

interface ChooseTreatmentContextType {
  allowedCannabisGramsQuota: number;
  cannabisLimits: CannabisLimitsResult | null;
  setShowExceededMaxQuotaModal: React.Dispatch<React.SetStateAction<boolean>>;
  showExceededMaxQuotaModal: boolean;
  
  totalQuantity: number;
  maxCartProducts: number;
  products: Product[];
  filteredProducts: Product[];
  selectedProducts: Product[];
  selectedProductItems: ProductItem[];
  selectedProductsCounter:number;
  handleProductToggle: (product: Product, toggleAction: "ADD" | "REMOVE") => void;
  handleProductItemToggle: (productItem: ProductItem, toggleAction: "ADD" | "REMOVE") => void;
  onSubmit: () => void;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  categoryLimitation: null | { monthlyLimitation: number; purchasedQuantity: number };
  submitLoading: boolean;
  filterData: { [key: string]: { value: string, isSelected: boolean, name: string, keyWordTitle: string, filterFn: (product: Product) => boolean }[] },
  toggleFilter: (category: string, value: string) => void;
  isFilterMenuOpen: boolean;
  setIsFilterMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetAllFilters: () => void;
  appliedFiltersCount: number;
  hidePrices: boolean;
  hideTags: boolean; //Hiding the Premium/Regular tags
  productCardRefs: React.RefObject<HTMLDivElement[]>;
  sortBy: SortByType,
  setSortBy: React.Dispatch<React.SetStateAction<SortByType>>,
}


export const ChooseTreatmentContext = createContext<ChooseTreatmentContextType | null>(null);

const CannaleoFreeDosageLayout = () => {


  const { country, language, category } = useParams() as {
    category: string;
    country: string;
    language: string;
  };

  const headerVersion = category.includes("medical_cannabis") ? "v2" : "v1";

  const initFilters = (products: ChooseTreatmentProductType[], predefinedFilters?: PredefinedFiltersType) => {
    const filterData: any = {}
    const parsedFiltersFnsMap: Record<string, { filterValue: string, filterFn: (product: ChooseTreatmentProductType) => boolean, name: string, keyWordTitle: string }[]> = {}
    
    if (predefinedFilters) {
      // format for efficient look up + create filtering functions
      const filterTypes = Object.keys(predefinedFilters); // e.g. ["thc", "cbd", "genetic"...]
      filterTypes.forEach((type: string) => {
        const filterType = type.toLowerCase();
        const predefinedFilter = predefinedFilters[filterType];
        const displayTitle = Object.keys(predefinedFilter)[0]; // e.g CBD-Gehalt, THC-Gehalt, Genetik, Effekte ...
        
        const pushFilter = (
          value: string,
          fn: (product: ChooseTreatmentProductType) => boolean,
          name: string
        ) => {
          parsedFiltersFnsMap[filterType].push({
            filterValue: value,
            filterFn: fn,
            name, // the name  will be displayed to the user
            keyWordTitle: displayTitle, // the keyWordTitle will be displayed to the user as well
          });
        };

        const filters = predefinedFilter[displayTitle];

        parsedFiltersFnsMap[filterType] = [];
        switch (filterType) {
          case "thc":
          case "cbd":
            filters.forEach((filter: { value: string, name: string }) => {
              const parsedRangeFilter = parseRangeFilter(filter.value); // a function that checks if a number is in a specific range (encoded by a string such as gte0-lte5)
              const filterFunction = (product: ChooseTreatmentProductType) => {
                const productAttribute = product.additional_data?.[filterType as keyof AdditionalData]; // extract value of position from additional data to filter by
                return parsedRangeFilter(productAttribute); // apply filter on extracted value e.g thc in range [0, 10], or cbd >= 15 ...
              }
              pushFilter(
                filter.value, // eg  gte0-lte5, gte5-lte15, gte15
                filterFunction,
                filter.name // eg Niedrig (<5%), Mittel (5-15%), Hoch (>15%)
              ); 
            });
            break;

          case "genetic":
            filters.forEach((filter: { value: string, name: string }) => {
              const { value: filterValue } = filter;
              const filterFunction = (product: ChooseTreatmentProductType) => {
                const genetic = product.additional_data?.[filterType as keyof AdditionalData];
                if (typeof genetic !== "string") return false;
                return genetic.toLowerCase() === filterValue.toLowerCase();
              }
              
              pushFilter(
                filter.value, // eg  "Sativa", "Indica", "Hybrid"
                filterFunction,
                filter.name // eg  "Sativa", "Indica", "Hybrid"
              ); 
            });

            // add the "all" option to genetic,
            pushFilter(
              "all",
              () => true,
              "Alle"
            );
            break;

          case "effects":
            filters.forEach((filter: { value: string, name: string }) => {
              const filterFn = (product: ChooseTreatmentProductType) => {
                const effects = product.additional_data?.[filterType as keyof AdditionalData];
                if (!Array.isArray(effects) || !effects.every((item) => typeof item === 'string') || effects.length === 0) return false;
                const normalizedValues = effects.map((item) => item.toLowerCase());
                return normalizedValues.includes(filter.value.toLowerCase());
              };              
              
              pushFilter(
                filter.value, // eg  "relaxed, sleepy, focussed", "happy"...
                filterFn,
                filter.name // eg  "Entspannend", "Gesellig", "Aufgeregt", Fokussiert" ...
              ); 
            });
            break;
        }
      });

      for (const product of products) {
        if (product.additional_data) {
          for (const [key, value] of Object.entries(product.additional_data)) {
            
            if (value) {
              // skip roprties we do not offer filters for
              if (!["thc", "cbd", "genetic", "effects"].includes(key)) {
                continue;
              }

              // Initialize filterData[key] if not already initialized
              if (!filterData[key]) {
                filterData[key] = [];
              }

              // Determine the filterValue based on the key (thc, cbd, genetic, effects)
              const filterObj = parsedFiltersFnsMap[key]?.find(({ filterValue, filterFn }) => filterFn(product));
              
              if (filterObj) {
                const filterOptions = [{
                  value: filterObj.filterValue?.toLowerCase() ?? "",
                  isSelected: false,
                  name: filterObj.name ?? "",
                  keyWordTitle: filterObj.keyWordTitle ?? "",
                  filterFn: filterObj.filterFn,
                }];

                filterData[key] = mergeArrays(filterData[key], filterOptions, (a: { value: string }) => a.value);
              }
            }
          }
        }
      }

      // sorting
      filterData.cbd &&= sortRangeFilter(filterData.cbd);
      filterData.thc &&= sortRangeFilter(filterData.thc);
    } 
    setFilterData(filterData);
  }


  const toggleFilter = (category: string, value: string, isMultiSelect: boolean = true) => {
    // thc, cbd, genetic, effects,
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      [category]: prevFilterData[category].map(filterObj => (
        {
          ...filterObj,
          isSelected:
            filterObj.value === value ?
              !filterObj.isSelected :
              isMultiSelect ? filterObj.isSelected : false
        }
      ))
    }));
  }

  const initStickyNotification = () => {
    if (searchParams.get("limit_disclaimer") && categoryLimitation) {
      const remainingPossibleQuantity = categoryLimitation?.monthlyLimitation - categoryLimitation?.purchasedQuantity;
      const content = `Ihr persönliches Limit ist derzeit: ${remainingPossibleQuantity > 0 ? remainingPossibleQuantity : 0} Gramm. Das generelle Limit beträgt ${categoryLimitation?.monthlyLimitation} Gramm pro Monat.`
      return { show: true, title: "Monatliches Limit überschritten", content, level: "disclaimer", refresh: false };
    } else {
      return { show: false, title: "", content: "", level: "", refresh: false };
    }
  }

  //States
  const navigate = useNavigate();
  const { getCart } = useCartContext();
  const cart = getCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const pharmacyFromParam = searchParams.get("epd") || cart?.external_pharmacy_id || null;
  
  const [products, setProducts] = useState<Product[]>([]);
  // const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const selectedProducts = useMemo(() => (products.filter(product => product.isSelected)), [products])
  const [selectedProductItems, setSelectedProductItems] = useState<ProductItem[]>([]);
  const [categoryLimitation, setCategoryLimitation] = useState<{ monthlyLimitation: number, purchasedQuantity: number } | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterData, setFilterData] = useState<{ [key: string]: { value: string, isSelected: boolean, name: string, keyWordTitle: string, filterFn: (product: Product) => boolean }[] }>({});
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stickyNotification, setStickyNotification] = useState<{ refresh: boolean, level: string, show: boolean, title: string, content: string }>(initStickyNotification())
  const [maxCartProducts, setMaxCartProducts] = useState<number>(1);
  const [selectedProductsCounter, setSetSelectedProductsCounter] = useState(0);
  const [selectedExternalPharmacy, setSelectedExternalPharmacy] = useState<ExternalPharmacy | null>(null);
  const [flow, setFlow] = useState<string | null>(null);
  const [allowedCannabisGramsQuota, setAllowedCannabisGramsQuota] = useState<number>(DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT);
  const [cannabisLimits, setCannabisLimits] = useState<CannabisLimitsResult | null>(null);
  const productCardRefs = useRef<HTMLDivElement[]>([]);
  const stockCodeFromParam = searchParams.get("sc") || null; //stock_code
  const [sortBy, setSortBy] = useState<SortByType>(null);

  const [desiredProductItem, setDesiredProductItem] = useState<ProductItem | null>(null);
  const [showExceededMaxQuotaModal, setShowExceededMaxQuotaModal] = useState<boolean>(false);


  const totalQuantity = useMemo(() => selectedProductItems.reduce((acc, item) => acc + item.quantity, 0), [selectedProductItems]);
  
  
  

  let API_PATH ="choose-treatment-free-dosage"
  if (pharmacyFromParam){
    API_PATH = "choose-treatment-external"
  }
  else if (stockCodeFromParam){
    API_PATH = "choose-treatment-by-stock"
  }

  // Memos 
  const appliedFiltersCount = useMemo(() => {
    let count = 0;

    if (!filterData) { 
      return 0;
    }

    // # 1 cannabis filters 
    const filterObjects = Object.values(filterData).flat();
    const activeFilters = filterObjects.filter((item) => item.isSelected);
    count += activeFilters.length;

    // # 2 sorting
    count+= sortBy ? 1 : 0;

    return count;
  }, [filterData, sortBy])


  const filteredProducts = useMemo(() => {

    let filtered: Product[];

    // # 1. Initially filtered are all the products minus the one that are already selected 
    filtered = products.filter((product) => !product.isSelected);


    // # 2 free search term
    if (searchTerm) {
      // Create a regex that allows any characters between words in the search term (case-insensitive).
      const regex = new RegExp(searchTerm.split(' ').join('.*'), 'i');
      filtered = filtered.filter((product) => regex.test(product.title));
    }

    // # 3 cannabis filters: effects, genetic, thc, cbd...
    if (filterData) {
      Object.keys(filterData).forEach((key) => {
        const whitelist = filterData[key].filter((item) => item.isSelected);
        
        if (!whitelist.length) return;
        filtered = filtered.filter((product) => whitelist.some(filterObj => filterObj.filterFn(product)));
      });
    }


    // # 4 sorting: no price filtering is offered in this flow
    if (sortBy) {
      switch (sortBy) {
        case SORT_BY_THC_ASC:
          filtered = sortProducts(filtered, (p1, p2) => (p1.additional_data?.thc ?? 0) - (p2.additional_data?.thc ?? 0));
          break;     
        case SORT_BY_THC_DESC:
          filtered = sortProducts(filtered, (p1, p2) => (p2.additional_data?.thc ?? 0) - (p1.additional_data?.thc ?? 0));
          break;     
        }
    }

    return filtered;

  }, [products, selectedProductItems, maxCartProducts, filterData, searchTerm, sortBy])


  // Fetch (GET)
  useEffect(() => {
    setLoading(true);
    http.getFromServer(`${API_PATH}/${category}/${country}/${language}?${searchParams.toString()}`,
      [
        http.MIDDLEWARES.CUSTOMER_ID,
        http.MIDDLEWARES.CART_ORDER
      ])
      .then((response) => {
        const serverData = response.data;
        const products = parseProducts(serverData.products, serverData.allowedCannabisGramsQuota);
        setProducts(products);
        setCategoryLimitation(serverData.categoryLimits);
        setSelectedExternalPharmacy(serverData?.externalPharmacy);
        const productsCopy = JSON.parse(JSON.stringify(serverData.products))
        initFilters(productsCopy, serverData.predefinedFilters);
        setDefaultProductsAndItems(productsCopy, serverData.allowedCannabisGramsQuota);
        setMaxCartProducts(serverData.maxCartProducts || 1);

        setAllowedCannabisGramsQuota(serverData.allowedCannabisGramsQuota);
        if (serverData.cannabisLimits) {
          setCannabisLimits(serverData.cannabisLimits);
        }

        let orderFlow = null;
        if (serverData.cart.external_pharmacy_id && searchParams.get("af")){
          if (searchParams.get("af") == CANNALEO){
            orderFlow = ORDER_FLOWS.CANNALEO_PRE_SELECTED
          }
          else if (searchParams.get("af") == FLOWZZ) {
            orderFlow = ORDER_FLOWS.FLOWZZ_PRE_SELECTED
          }
        }
        else if (searchParams.get("sc")) {
          orderFlow = ORDER_FLOWS.STOCK + "_" + searchParams.get("sc")
        }

        setFlow(orderFlow);
      })
      .catch((error) => {
        setError(500);
        window.clientLogger.error("error get CannaleoFreeDosageLayout", error)
      })
      .finally(() => {
        setLoading(false);
      })
  }, [])


  useEffect(() => {
    setStickyNotification(initStickyNotification());

    if (searchParams.get("mcpqle") === "true") {
      setShowExceededMaxQuotaModal(true);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("mcpqle");
      setSearchParams(newSearchParams);
    }


  }, [searchParams, categoryLimitation, allowedCannabisGramsQuota])


  const rateLimited = useMemo(() => isOrderRateLimited(cannabisLimits), [cannabisLimits]);
  const cannabisLimitMessage = useMemo(() => getCannabisLimitMessage(cannabisLimits, language), [cannabisLimits, language]);

  const onSubmit = async () => {
    setSubmitLoading(true);


    // 1. Mix panel events
    selectedProducts.forEach((product, index) => {
      MainTracker.superProperties({ [`Product Name ${index + 1}`]: product.title, "Purchase Type": "Regular" });
    })

    selectedProductItems.forEach((productItem, index) => {
      MainTracker.superProperties({ [`ProductItemID ${index + 1}`]: productItem.product_item_id });
    })
    
    // 2. Send to server
    http.postToServer("choose-treatment-free-dosage", {
      flow,
      epd:searchParams.get("epd"),
      integrationType:searchParams.get("af"),
      cartProductsDTOs: selectedProductItems.map((item) => {
        return {
          quantity: 1,
          product_in_stock: item.in_stock,
          only_prescription: true,
          catalog_id: item.catalog_id,
          product_item_id: item.product_item_id,
          is_pre_selected: item.preSelected
        }
      })
    }, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
      .then((response) => {

        let customerFromSession = loadCustomerFromSession();
        const updatedSearchParams = new URLSearchParams(searchParams); 
        const deeplinkCoupon = window.eventsTracking.getData("cpn")
        if (deeplinkCoupon) {
          updatedSearchParams.set("cpn", deeplinkCoupon);
        }

        if (!customerFromSession) {
          // if no customer id send to registration
          navigate({ pathname: `/sign_up/${country}/${language}`, search: updatedSearchParams.toString() });
        } else {
          trackContinueToShipping();
          navigate({ pathname: `/shipping/${country}/${language}`, search: updatedSearchParams.toString() });
        }
      })
      .catch((err) => {
        const code = err?.response?.data?.code;

        if (CANNABIS_LIMIT_ERROR_CODES.includes(code)) {
          const content = cannabisLimitMessage || translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_limit');
          setStickyNotification((current) => ({ show: true, title: "", content, level: "disclaimer", refresh: !current.refresh }));
        } else {
          setStickyNotification((current) => ({ show: true, title: "Server Failed", content: "", level: "error", refresh: !current.refresh }));
        }
      })
      .finally(() => setSubmitLoading(false));
  }


  const setDefaultProductsAndItems = (products: Product[], allowedCannabisGramsQuota: number) => {


    // 1. Pre-select all Products:
    const preSelectedProducts = products.filter((product) => product.preSelected);
    const totalPreSelectedQuantity = preSelectedProducts.reduce((acc, cur ) => cur.items.find(item => item.preSelected)?.quantity || 0 + acc, 0);

    if (totalPreSelectedQuantity > allowedCannabisGramsQuota) {
      setProducts(products => products.sort((a, b) => {
        const isAPreSelected = Boolean(preSelectedProducts.find(product => product.product_id === a.product_id));
        const isBPreSelected = Boolean(preSelectedProducts.find(product => product.product_id === b.product_id));
        if (isAPreSelected && !isBPreSelected) {
          return -1;
        }
        if (!isAPreSelected && isBPreSelected) {
          return 1;
        }
        return 0;
      }))

      setShowExceededMaxQuotaModal(true);
      return;
    }

    setSetSelectedProductsCounter(preSelectedProducts.length);

    // // 2. Pre-select all Product Items:
    const preSelectedProductItems: ProductItem[] = [];
    preSelectedProducts.forEach((product) => {
      let isProductItemSelected = false
      product.items.forEach((item) => {
        if (item.preSelected) {
          preSelectedProductItems.push(item);
          isProductItemSelected = true
        }
      })
      // fallback - if product is selected but product item not selected (on server side) - let select the the 1-4 product item
      if (!isProductItemSelected) {
        const filteredProductItems = product.items.filter((item: any) => item.in_stock && item.quantity < (allowedCannabisGramsQuota - totalQuantity))
        const medianItemIndex = Math.floor(filteredProductItems.length / 4);
        preSelectedProductItems.push(filteredProductItems[medianItemIndex]);
      }
    })
    setSelectedProductItems(preSelectedProductItems);
  }


  const handleProductItemToggle = (selectedProductItem: ProductItem, toggleAction: "ADD" | "REMOVE") => {
    if (toggleAction === "ADD") {

      setSelectedProductItems((current) => {

        // 1. If user may choose only 1 then return only selected product item
        if (maxCartProducts === 1) {
          return [selectedProductItem]
        }

        // 2. Else, get all the selected product items that are not from the same product
        let selectedWithoutSiblings = current.filter((currentItem) => currentItem.catalog_id !== selectedProductItem.catalog_id);

        // 3. Add the selectedProductItem
        return [...selectedWithoutSiblings, selectedProductItem]
      });

    } else {
      //Remove that product item from selected
      setSelectedProductItems((current) => current.filter((item) => item.product_item_id !== selectedProductItem.product_item_id));
    }
  }


  const handleProductToggle = useCallback((selectedProduct: Product, toggleAction: "ADD" | "REMOVE") => {
    // First, verify if we can add more products to the cart 
    // In case of maxProducts === 1, we want to always let select something else
    if (maxCartProducts !== 1 && (selectedProducts.length >= maxCartProducts) && toggleAction === "ADD") {
      return
    }

    if (toggleAction === "ADD" && (rateLimited || totalQuantity > allowedCannabisGramsQuota)) {
      setShowExceededMaxQuotaModal(true);
      return;
    }

    if (toggleAction === "ADD") {

      setSetSelectedProductsCounter(current => maxCartProducts === 1 ? 1 : current + 1);

      // 2. Select the product (with some timeout for the HoverCard animation)
      setTimeout(() => {
        setProducts((products) => {
          return products.map((product) => {
            if (maxCartProducts === 1) {
              return product.product_id === selectedProduct.product_id
                ? { ...product, isSelected: true }
                : { ...product, isSelected: false }; //Disable all other products if maxProductCart === 1
            }
            else {
              return product.product_id === selectedProduct.product_id
                ? { ...product, isSelected: true }
                : product; //Keep the other products as they are (MultiSelect)
            }
          });
        });

      }, 1100);
      

      // 3. Select The median productItem by default
      const filteredProductItems = selectedProduct.in_stock ? selectedProduct.items.filter((item: any) => item.in_stock) : selectedProduct.items;
      let productItemIndex = Math.floor(filteredProductItems.length / 4);

      if (filteredProductItems[productItemIndex]?.quantity) {
        productItemIndex = filteredProductItems.findIndex((item) => item.quantity <= (allowedCannabisGramsQuota - totalQuantity));
      }

      handleProductItemToggle(filteredProductItems[productItemIndex], toggleAction);


    } else { // Toggle action = REMOVE

      setSetSelectedProductsCounter(current => current - 1);

      setProducts(current => {
        return current.map(product =>  (product.product_id === selectedProduct.product_id ? {...product, isSelected: false} : product))
      })

      setSelectedProductItems((current) => current.filter((selectedProductItem) => selectedProductItem.catalog_id !== selectedProduct.catalog_id));
    }

  }, [selectedProducts,selectedProductsCounter, maxCartProducts, selectedProductItems, submitLoading, allowedCannabisGramsQuota, totalQuantity])


  const scrollToMainTitle = useCallback(() => {
    const headerSearch = document.querySelector("#header-disclaimer");
    headerSearch?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  //   headerSearch?.scrollTo({
  //     behavior: "smooth",
  //     top: 15,
  // });
    
    const productsPanelTitle = document.querySelector("#left-panels");
    
    if (productsPanelTitle) {
      productsPanelTitle.scrollTo({
            behavior: "smooth",
            top: 0,
        });
    }
  },[selectedProducts]);

  const scrollToSelectedProduct = useCallback(() => {
    const productCard = productCardRefs.current.find(el => Number(el?.dataset.productId) === Number(selectedProducts[0].product_id));
    productCard?.scrollIntoView({
      behavior: "smooth", // Smooth scrolling
      block: "center",    // Align to the top of the viewport
    });

    // const rect = productCard?.getBoundingClientRect();
    // if (rect) {
    //   window?.scrollTo({
    //     top: rect.top + window.pageYOffset - 120,
    //     behavior: "smooth",
    //   });
    // }

   
  },[selectedProducts]);


  const parseProducts = (products: Product[], allowedCannabisGramsQuota: number) => {

    const preSelectedProducts = products.filter((product) => product.preSelected);
    const totalPreSelectedQuantity = preSelectedProducts.reduce((acc, cur ) => cur.items.find(item => item.preSelected)?.quantity || 0 + acc, 0);

    let setIsSelected = true;
    if (totalPreSelectedQuantity > allowedCannabisGramsQuota) {

      setProducts(products => products.sort((a, b) => {
        const isAPreSelected = Boolean(preSelectedProducts.find(product => product.product_id === a.product_id));
        const isBPreSelected = Boolean(preSelectedProducts.find(product => product.product_id === b.product_id));
        if (isAPreSelected && !isBPreSelected) {
          return -1;
        }
        if (!isAPreSelected && isBPreSelected) {
          return 1;
        }
        return 0;
      }))

      setShowExceededMaxQuotaModal(true);
      setIsSelected = false;
    }

    const parsedProducts = products.map((product: Product) => {
      return {
      ...product, 
      isSelected: setIsSelected?product.preSelected:false,
      minPricePerQuantity: getMinPricePerQuantity(product),
    }});


    return parsedProducts;
  }

  const freeDosageContext = {
    totalQuantity,
    showExceededMaxQuotaModal,
    setShowExceededMaxQuotaModal,
    allowedCannabisGramsQuota,
    cannabisLimits,
    desiredProductItem,
    setDesiredProductItem,
    maxCartProducts,
    products,
    selectedProducts,
    selectedProductsCounter,
    selectedProductItems,
    handleProductToggle,
    handleProductItemToggle,
    searchTerm,
    setSearchTerm,
    onSubmit,
    categoryLimitation,
    submitLoading,
    filterData,
    toggleFilter,
    isFilterMenuOpen,
    setIsFilterMenuOpen,
    appliedFiltersCount,
    filteredProducts,
    sortBy,
    setSortBy,
    hideTags: Boolean(cart?.external_pharmacy_id),
    hidePrices: Boolean(cart?.external_pharmacy_id),
    resetAllFilters: () => {
      setFilterData(prev =>
        Object.keys(prev).reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: prev[curr].map(item => ({
              ...item,
              isSelected: false,
            })),
          }),
          {} as typeof filterData
        )
      );
      setSortBy(null);
    },
    productCardRefs,
  }

  const pharmacyName = selectedExternalPharmacy?.name || null;
  const pharmacyUnofficialName = selectedExternalPharmacy?.cannabis_pharmacy_name || null;
        
  const pharmacyTitle = pharmacyName
  ? ` - ${pharmacyName} ${pharmacyUnofficialName ?  "(" + pharmacyUnofficialName + ")" : ""}`
  : "";


  return (
    <ChooseTreatmentContext.Provider value={freeDosageContext}>
      {loading && <LoadingModal />}

      <StickyNotification
        showNotification={stickyNotification.show}
        title={stickyNotification.title}
        onClick={() => setStickyNotification({ show: false, title: "", content: "", level: "", refresh: false })}
        content={stickyNotification.content}
        level={stickyNotification.level}
        refresh={stickyNotification.refresh}
      />

      {error && (
        <>
          <HeaderController showSteps={false} stage={"Questionnaire"} headerVersion={headerVersion}/>
          <ServerError status={error} />
        </>
      )}

      {!loading && !error &&

        <>

          {/* Exceeded Max Quota Modal */}

          <PortaledModal
          shouldBackgroundClickHideModal={true}
          shouldShowModal={showExceededMaxQuotaModal}
          backgroundColor='transparent'
          hideModal={() => setShowExceededMaxQuotaModal(false)}
          showModal={() => setShowExceededMaxQuotaModal(true)}
        >
          <div id="exceeded-max-quota-modal-disclaimer" className="relative">
            <Disclaimer
              text = {rateLimited ? cannabisLimitMessage : translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_quota').replace('<maxQuota>', String(cannabisLimits?.monthlyLimit || DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT)).replace('<quota>', String(allowedCannabisGramsQuota))}
              containerClassName="w-[90%] sm:w-[464px] min-h-[120px] sm:min-h-[161px] bg-white border-2 border-solid border-[#269C6E] rounded-lg p-3 sm:p-4 pt-4 sm:pt-6 flex items-start gap-2 sm:gap-3"
              textClassName="text-[#384248] font-normal text-sm sm:text-base mt-1 sm:mt-2"
              iconClassName="text-[#00B388] w-4 h-4 sm:w-5 sm:h-5 mt-1"
            ></Disclaimer>
          </div>
        </PortaledModal>


          {/* DoktorABC Nav  */}
          {category && <HeaderController language={language} stage="Treatment" headerVersion={headerVersion}/>}

          {/* Main container  */}
          <main id='main-container' className='flex min-h-screen pt-10 p-4 bg-[var(--primary-gray)] flex-col items-center'>

            {/* Content Container  */}
            <div id='content-container' className='md:max-w-[500px] flex flex-col items-center justify-center '>

              {/* Header  */}
              <header className='w-full flex flex-col gap-y-2 mb-6'>
                <h1 id='main-title' className='flex text-2xl lg:text-4xl justify-center text-[#0D2C54]'> {translate(language, 'choose_treatment', 'default/choose_preferred_treatment') || 'Behandlung wählen'} </h1>
                {freeDosageContext.hideTags ? null : <Legend />}
              </header>


              {/* Disclaimer  */} 
              <p id='header-disclaimer' style={{ fontFamily: "Roboto, sans-serif" }} className='w-full mb-4 text-[#0D2C54] text-[16px] font-medium break-words'>
                    <span className='text-[#0A9281]'>{"* "}</span>
                    Nachfolgend finden Sie alle in der von Ihnen ausgewählten Apotheke verfügbaren Produkte
                    {pharmacyTitle}
                </p>


              {/* Search & Desktop disclaimer  */}
              <div id='search-and-disclaimer' style={{ boxShadow: "0 25px 25px #f7f7f7", backgroundColor: "#f7f7f7", zIndex: 40 }} className='sticky top-0 pin-below-app-bar w-full flex flex-col gap-1 mb-5 pt-2'>

              
                  
                <div id='header-search'  className='flex flex-row w-full justify-between items-center gap-4'>
                  <ProductSearch className={"flex-1"} />
                  <ProductFiltersControl />
                </div>
                  
              </div>


              {/* Mobile Disclaimer And Panels Container  */}
              <div id='mobile-disclaimer-and-panels-container' className='w-full'>

                  

                {/* Panels Container  */}
                <div id='panels-container' className='grid grid-col-1 md:grid-cols-1 gap-8 w-full ' >
                  {/* left panel */}
                  <div id='left-panels' className='flex flex-col  flex-1 w-[] overflow-y-auto'>

                    {/* Selected Products (Only for multiple select) */}
                    {
                      maxCartProducts  ?
                      <>
                        <span id='selected-products-panel-title' style={{ fontFamily: "Roboto, sans-serif" }} className='font-medium mb-2  text-[13px] text-[#98A0AF] mt-2'> Ihr Warenkorb</span>
                        <ProductSelectionPanel products={selectedProducts} languageID={language}  />
                      </>
                      :
                      null
                    }

                    {/* All Products  */}
                      <span id='all-products-panel-title' style={{ fontFamily: "Roboto, sans-serif" }} className='font-medium mb-6  text-[13px] text-[#98A0AF]'>
                        {flow === ORDER_FLOWS.FLOWZZ_PRE_SELECTED ? (
                          <>{translate(language, 'choose_treatment', 'default/all_products_flowzz') || 'In derselben Lieferung (ohne Aufpreis) können Sie außerdem bestellen'} <span id='filtered-products-amount' className='notranslate'> ({filteredProducts.length}) </span>:</>
                        ) : (
                          <>{translate(language, 'choose_treatment', 'default/all_products') || 'Alle Produkte'} <span id='filtered-products-amount' className='notranslate'> ({filteredProducts.length}) </span></>
                        )}
                      </span>
                      <ProductSelectionPanel products={filteredProducts} languageID={language} />

                  </div>

                </div>
              </div>

            </div>
          </main>

          {/* DoktorABC Footer  */}
          {category && <Footer showDiscountDisclaimer={true} page="ChooseTreatment" language={language} />} {/*shows footer in normal flow but not in modal*/}

          {/* Filters Menu  */}
          <FilterMenu filterData={filterData} />
 
          {/* Sticky Submit  */}
          <StickySubmitButton
            showDisclaimer={false}
            disclaimerText={(translate(language, 'choose_treatment', 'cannabis_limits/max_products_reached') as string).replace('<maxProducts>', String(maxCartProducts))}
            buttonDisabled={submitLoading || selectedProducts.length === 0}
            onSubmit={onSubmit}
            showAmountOfSelectedProducts={true}
            flow={flow}
            maxSelectedProductsAmount={maxCartProducts}
            selectedProductsAmount= {selectedProductsCounter}
            // onArrowClick={Boolean(searchParams.get("epd")) ? scrollToMainTitle : undefined }
            onCartClick={Boolean(searchParams.get("epd")) ? scrollToMainTitle : scrollToSelectedProduct }
          />
          

        </>
      }
    </ChooseTreatmentContext.Provider >
  )
}

export default CannaleoFreeDosageLayout
